import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import LineItem from "./LineItemWidget";
import LineItemGroup from "./LineItemGroupWidget";

const headingOrOrderNameInput = (hasMultipleVariations, name, error, onOrderFormVariationNameChangeCallback) => {
  // Since most of the providers only have one order form variation, then it doesn't make sense
  // to show an input field to update the form name
  if (!hasMultipleVariations) {
    return <span />;
  }

  return (
    <input
      type="text"
      className={`order-name-input${error ? " error" : ""}`}
      placeholder="custom 'plans & a-la-carte options' set name"
      value={name}
      onChange={event => onOrderFormVariationNameChangeCallback(event.target.value)}
    />
  );
};

const getPropertyPathValue = (source, path) => {
  let value = source;

  for (let i = 0; i < path.length; i += 1) {
    if (typeof value[path[i]] === "undefined") {
      return null;
    }

    value = value[path[i]];
  }

  return value;
};

const sampleUrlBuilder = (providerId, providerDomain, optionalOrderFormName, optionalPreselectedPlanId) => {
  // Intentionally empty. This is for understanding how the url params work.
  const customerId = "";
  const orderFormName = optionalOrderFormName ? encodeURIComponent(optionalOrderFormName) : "";

  const baseUrl = `https://${providerDomain}/public/api/orderForm/standard/${providerId}/${customerId}/${orderFormName}`;
  // Remove trailing slashes /.
  // Some of the url params could be blank, and the url would end up like ../orderForm/standard//
  const cleanUrl = baseUrl.replace(/\/*$/, "");

  if (optionalPreselectedPlanId) {
    return `${cleanUrl}?tourType=${optionalPreselectedPlanId}`;
  }

  return cleanUrl;
};

const TourTypeFooter = ({ sampleUrl }) => <span className="help">Preselected by using: {sampleUrl}</span>;

TourTypeFooter.propTypes = {
  sampleUrl: PropTypes.string.isRequired,
};

const exampleUrlOrNull = (hasMultipleVariations, sampleUrl) => {
  if (hasMultipleVariations) {
    return (
      <div className="help">
        You can leave one variation without name so it can be used <br />
        as the default order form that will be accessible under <br />
        {sampleUrl}
      </div>
    );
  }

  return <div className="help">access url example: {sampleUrl}</div>;
};

const OrderFormVariation = observer(
  ({
    hasMultipleVariations,
    name,
    tourTypes,
    extras,
    services,
    errors,
    providerId,
    providerDomain,
    onVariationNameChangeCallback,
    onLineItemInputChangeCallback,
    onServiceLabelChangeCallback,
    onAddLineItemCallback,
    onAddLineItemToServiceCallback,
    onAddServiceCallback,
    onRemoveServiceCallback,
    onRemoveLineItemCallback,
    onRemoveOrderFormVariationCallback,
  }) => (
    <div className="provider-order">
      {headingOrOrderNameInput(
        hasMultipleVariations,
        name,
        getPropertyPathValue(errors, ["name"]),
        onVariationNameChangeCallback,
      )}
      {exampleUrlOrNull(hasMultipleVariations, sampleUrlBuilder(providerId, providerDomain, name))}

      {hasMultipleVariations && (
        <a
          className="order-remove delete"
          href="#"
          onClick={event => {
            event.preventDefault();
            onRemoveOrderFormVariationCallback();
          }}
        >
          Remove Custom Set
        </a>
      )}

      {hasMultipleVariations && <div className="ui divider" />}

      {/* Tour Types Section */}

      <div className="section-title">
        <h3>Plans.</h3>Customer required to select one.
      </div>
      {tourTypes.map(tourType => (
        <LineItem
          key={tourType.id}
          description={tourType.description}
          footer={
            tourType.description ? (
              <TourTypeFooter sampleUrl={sampleUrlBuilder(providerId, providerDomain, name, tourType.id)} />
            ) : null
          }
          descriptionError={getPropertyPathValue(errors, ["tourTypes", tourType.id, "description"])}
          price={tourType.price}
          priceError={getPropertyPathValue(errors, ["tourTypes", tourType.id, "price"])}
          removable={tourTypes.length > 1}
          onChangeCallback={(property, value) => onLineItemInputChangeCallback(tourType.id, property, value)}
          onRemoveCallback={() => onRemoveLineItemCallback(tourType.id)}
        />
      ))}
      <br />
      <a
        href="#"
        onClick={event => {
          event.preventDefault();
          onAddLineItemCallback("tourTypes");
        }}
      >
        <i className="add icon" style={{ textDecoration: "none" }} />
        Add Plan
      </a>

      {/* Extras Section */}

      <div className="ui divider" />

      <div className="section-title">
        <h3>A-la-carte options. </h3> Customer will select each individually.
      </div>

      {extras.map(extra => (
        <LineItem
          key={extra.id}
          description={extra.description}
          descriptionError={getPropertyPathValue(errors, ["extras", extra.id, "description"])}
          price={extra.price}
          priceError={getPropertyPathValue(errors, ["extras", extra.id, "price"])}
          removable
          onChangeCallback={(property, value) => onLineItemInputChangeCallback(extra.id, property, value)}
          onRemoveCallback={() => onRemoveLineItemCallback(extra.id)}
        />
      ))}
      <br />
      <a
        href="#"
        onClick={event => {
          event.preventDefault();
          onAddLineItemCallback("extras");
        }}
      >
        <i className="add icon" style={{ textDecoration: "none" }} />
        Add a-la-carte checkbox item
      </a>

      {/* Services Section */}

      <div className="section-title">
        <br />
        Customer will select one from a list.
      </div>
      {services.map(service => (
        <LineItemGroup
          key={service.id}
          label={service.label}
          labelError={getPropertyPathValue(errors, ["services", service.id, "label"])}
          onLabelChangeCallback={label => onServiceLabelChangeCallback(service.id, label)}
          onRemoveCallback={() => onRemoveServiceCallback(service.id)}
        >
          {service.lineItems.map(lineItem => (
            <LineItem
              key={lineItem.id}
              description={lineItem.description}
              descriptionError={getPropertyPathValue(errors, [
                "services",
                service.id,
                "lineItems",
                lineItem.id,
                "description",
              ])}
              price={lineItem.price}
              priceError={getPropertyPathValue(errors, ["services", service.id, "lineItems", lineItem.id, "price"])}
              removable={service.lineItems.length > 1}
              onChangeCallback={(property, value) => onLineItemInputChangeCallback(lineItem.id, property, value)}
              onRemoveCallback={() => onRemoveLineItemCallback(lineItem.id)}
            />
          ))}
          <a
            href="#"
            onClick={event => {
              event.preventDefault();
              onAddLineItemToServiceCallback(service.id);
            }}
          >
            <i className="add icon" style={{ textDecoration: "none" }} />
            Add Option
          </a>
        </LineItemGroup>
      ))}

      <a
        className="add-optional-service"
        href="#"
        onClick={event => {
          event.preventDefault();
          onAddServiceCallback();
        }}
      >
        <i className="add icon" style={{ textDecoration: "none" }} />
        Add a-la-carte dropdown menu item
      </a>
    </div>
  ),
);

OrderFormVariation.propTypes = {
  // data
  name: PropTypes.string,
  tourTypes: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  extras: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  services: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  errors: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  providerId: PropTypes.number.isRequired,
  providerDomain: PropTypes.string.isRequired,
  hasMultipleVariations: PropTypes.bool,
  // callbacks
  onVariationNameChangeCallback: PropTypes.func.isRequired,
  onLineItemInputChangeCallback: PropTypes.func.isRequired,
  onServiceLabelChangeCallback: PropTypes.func.isRequired,
  onAddServiceCallback: PropTypes.func.isRequired,
  onRemoveServiceCallback: PropTypes.func.isRequired,
  onAddLineItemCallback: PropTypes.func.isRequired,
  onAddLineItemToServiceCallback: PropTypes.func.isRequired,
  onRemoveLineItemCallback: PropTypes.func.isRequired,
  onRemoveOrderFormVariationCallback: PropTypes.func.isRequired,
};

export default OrderFormVariation;
